import access from "../content/access.json";
import { escapeHtml } from "./md";
import { track } from "../systems/analytics";
import { roomById } from "../brand/club";

type Filter = "all" | "clinics" | "pharmacies";
type ClinicStatus = "active" | "scheme" | "platform" | "unverified" | "suspended" | "closed" | "alias";

const STATUS_LABEL: Partial<Record<ClinicStatus, string>> = {
  scheme: "Access scheme",
  platform: "Platform",
  unverified: "Unverified",
  suspended: "Suspended",
  closed: "Closed",
  alias: "Former name",
};

export function mountAccess(host: HTMLElement) {
  let filter: Filter = "all";
  let query = "";
  let showInactive = false;

  const matches = (hay: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return hay.toLowerCase().includes(q);
  };

  const paintLists = () => {
    const clinics = access.clinics.filter((c) => {
      const status = ((c as { status?: string }).status ?? "active") as ClinicStatus;
      if (!showInactive && status !== "active" && status !== "scheme" && status !== "platform") {
        return false;
      }
      return matches(
        `${c.name} ${c.summary} ${c.focus} ${c.notes} ${(c as { region?: string }).region ?? ""} ${status}`,
      );
    });
    const pharmacies = access.pharmacies.filter((p) =>
      matches(`${p.name} ${p.summary} ${p.notes}`),
    );

    const clinicsSection = host.querySelector("[data-clinics-section]") as HTMLElement | null;
    const pharmSection = host.querySelector("[data-pharm-section]") as HTMLElement | null;
    const clinicsEl = host.querySelector("[data-clinics]") as HTMLElement | null;
    const pharmEl = host.querySelector("[data-pharmacies]") as HTMLElement | null;
    const clinicsCount = host.querySelector("[data-clinics-count]");
    const pharmCount = host.querySelector("[data-pharm-count]");

    if (clinicsSection) clinicsSection.hidden = filter === "pharmacies";
    if (pharmSection) pharmSection.hidden = filter === "clinics";
    if (clinicsCount) clinicsCount.textContent = String(clinics.length);
    if (pharmCount) pharmCount.textContent = String(pharmacies.length);

    if (clinicsEl) {
      clinicsEl.innerHTML = "";
      if (!clinics.length) {
        clinicsEl.innerHTML = `<p class="learn-empty">No clinics matched that filter.</p>`;
      } else {
        clinics.forEach((c) => {
          const status = ((c as { status?: string }).status ?? "active") as ClinicStatus;
          const region = (c as { region?: string }).region;
          const statusLabel = STATUS_LABEL[status];
          const card = document.createElement("article");
          card.className = `annex-card${status !== "active" && status !== "scheme" ? " annex-card--muted" : ""}`;
          card.innerHTML = `
            <p class="annex-card-kind">Clinic · ${escapeHtml(c.regulator)}${statusLabel ? ` · ${escapeHtml(statusLabel)}` : ""}</p>
            <h3>${escapeHtml(c.name)}</h3>
            ${region ? `<p class="annex-card-region">${escapeHtml(region)}</p>` : ""}
            <p class="annex-card-sum">${escapeHtml(c.summary)}</p>
            <p class="annex-card-focus">${escapeHtml(c.focus)}</p>
            ${c.priceNote ? `<p class="annex-card-price">${escapeHtml(c.priceNote)}</p>` : ""}
            ${c.notes ? `<p class="annex-card-notes">${escapeHtml(c.notes)}</p>` : ""}
            <div class="annex-card-actions">
              <a class="annex-link" href="${escapeHtml(c.url)}" target="_blank" rel="noopener noreferrer">Clinic site</a>
              <a class="annex-link annex-link--verify" href="${escapeHtml(c.verifyUrl)}" target="_blank" rel="noopener noreferrer">Verify on ${escapeHtml(c.regulator)}</a>
              <a class="annex-link annex-link--source" href="https://medbud.wiki/clinics/" target="_blank" rel="noopener noreferrer">MedBud</a>
            </div>
          `;
          clinicsEl.appendChild(card);
        });
      }
    }

    if (pharmEl) {
      pharmEl.innerHTML = "";
      if (!pharmacies.length) {
        pharmEl.innerHTML = `<p class="learn-empty">No pharmacies matched that filter.</p>`;
      } else {
        pharmacies.forEach((p) => {
          const card = document.createElement("article");
          card.className = "annex-card annex-card--pharm";
          card.innerHTML = `
            <p class="annex-card-kind">Pharmacy · ${escapeHtml(p.regulator)}</p>
            <h3>${escapeHtml(p.name)}</h3>
            <p class="annex-card-sum">${escapeHtml(p.summary)}</p>
            ${p.notes ? `<p class="annex-card-notes">${escapeHtml(p.notes)}</p>` : ""}
            <div class="annex-card-actions">
              <a class="annex-link" href="${escapeHtml(p.url)}" target="_blank" rel="noopener noreferrer">Pharmacy site</a>
              <a class="annex-link annex-link--verify" href="${escapeHtml(p.verifyUrl)}" target="_blank" rel="noopener noreferrer">Verify on ${escapeHtml(p.regulator)}</a>
            </div>
          `;
          pharmEl.appendChild(card);
        });
      }
    }

    host.querySelectorAll("[data-annex-filter]").forEach((btn) => {
      const el = btn as HTMLElement;
      el.setAttribute("aria-pressed", el.dataset.annexFilter === filter ? "true" : "false");
    });
  };

  const annex = roomById("access");
  host.innerHTML = `
    <div class="club-room-banner">
      <h2>${escapeHtml(annex.name)}</h2>
      <p>${escapeHtml(annex.host)}</p>
    </div>

    <aside class="annex-disclaimer" role="note">
      <p class="annex-disclaimer-kicker">House rule</p>
      <p>${escapeHtml(access.disclaimer)}</p>
      <p class="annex-updated">Directory updated ${escapeHtml(access.updated)}</p>
    </aside>

    <section class="annex-section">
      <p class="bar-rail-label">How the pipe works</p>
      <ol class="annex-steps">
        ${access.howItWorks.map((step) => `<li>${escapeHtml(step)}</li>`).join("")}
      </ol>
    </section>

    <div class="annex-toolbar">
      <label class="annex-search">
        <span class="visually-hidden">Filter directory</span>
        <input type="search" placeholder="Filter clinics & pharmacies…" data-annex-q />
      </label>
      <div class="annex-filters" role="group" aria-label="Directory filter">
        <button type="button" class="filter-pill" data-annex-filter="all" aria-pressed="true">All</button>
        <button type="button" class="filter-pill" data-annex-filter="clinics" aria-pressed="false">Clinics</button>
        <button type="button" class="filter-pill" data-annex-filter="pharmacies" aria-pressed="false">Pharmacies</button>
      </div>
    </div>

    <section class="annex-section" data-clinics-section>
      <p class="bar-rail-label">Clinics <span class="annex-count" data-clinics-count></span></p>
      <p class="paths-lead">Public-literacy snapshot enriched with <a href="https://medbud.wiki/clinics/" target="_blank" rel="noopener noreferrer">MedBud UK</a> clinic rows (locations, former names, schemes). Not a ranking, not exhaustive forever — fees on MedBud rot. Verify on CQC (or the right national regulator).</p>
      <label class="annex-toggle">
        <input type="checkbox" data-show-inactive />
        Include closed / suspended / former names
      </label>
      <div class="annex-grid" data-clinics></div>
    </section>

    <section class="annex-section" data-pharm-section>
      <p class="bar-rail-label">Specialty pharmacies <span class="annex-count" data-pharm-count></span></p>
      <p class="paths-lead">Specialty pharmacies appearing in UK CBPM literacy directories. GPhC’s 2025 review counted ~25 regular suppliers without publishing names — this list is literacy, not that census. CBD shops are a different door. Verify on GPhC.</p>
      <div class="annex-grid" data-pharmacies></div>
    </section>

    <section class="annex-section">
      <p class="bar-rail-label">Sources & registers</p>
      <ul class="annex-sources">
        ${access.sources
          .map(
            (s) =>
              `<li><a href="${escapeHtml(s.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(s.label)}</a></li>`,
          )
          .join("")}
      </ul>
    </section>
  `;

  host.querySelectorAll("[data-annex-filter]").forEach((btn) => {
    btn.addEventListener("click", () => {
      filter = (btn as HTMLElement).dataset.annexFilter as Filter;
      track("annex_filter", { filter });
      paintLists();
    });
  });

  const qEl = host.querySelector("[data-annex-q]") as HTMLInputElement;
  qEl.addEventListener("input", () => {
    query = qEl.value;
    paintLists();
  });

  host.querySelector("[data-show-inactive]")?.addEventListener("change", (e) => {
    showInactive = (e.target as HTMLInputElement).checked;
    track("annex_inactive", { show: showInactive ? 1 : 0 });
    paintLists();
  });

  paintLists();
}
